import * as React from 'react';
import {useMergeRefs} from '@floating-ui/react';
import {noop} from 'lodash';

import {useVirtualizer} from '@packages/systems/spring-hooks';
import {emptyObject} from '@packages/utilities/fp/utils';

import {mergeProps} from '../../../utils';

import {RowItemAction, RowItemActions, RowItemAdornment} from '../../RowItem';
import type {
  RowItemProps,
  RowItemAdornmentProps,
  RowItemActionProps,
} from '../../RowItem';

import type {OptionGroupType, OptionType, SearchableListProps} from '../_types';
import type {SearchableListBoxProps} from '../SearchableListBox';

import {
  SearchableListItemContext,
  useSearchableListItem,
  useSearchableListItemContext,
} from './useSearchableListItem';
import {SearchableListItemBodyView} from './SearchableListItem.styles';

export interface SearchableListItemProps extends Omit<
  RowItemProps,
  | 'isSelected'
  | 'onClick'
  | 'shouldAllowRenaming'
  | 'isRenaming'
  | 'onRenameOpenChange'
> {}

export const SearchableListItemBody = SearchableListItemBodyView;

export const SearchableListItemActions = RowItemActions;

export const SearchableListItemAction = React.forwardRef<
  HTMLButtonElement,
  RowItemActionProps
>(({...props}, ref) => <RowItemAction ref={ref} {...props} />);

export const SearchableListItemAdornment = ({
  isInteractive,
  ...props
}: RowItemAdornmentProps) => {
  const {isActive} = useSearchableListItemContext();

  return (
    <RowItemAdornment
      {...props}
      isInteractive={isActive ? isInteractive : false}
    />
  );
};

/**
 * SearchableListItem
 *
 * A wrapper component for rendering custom SearchableList options.
 *
 * ## Usage
 *
 * ```jsx
 * import {SearchableListItem} from '@packages/systems/spring';
 *
 * const renderOption = (option, state) => (
 *   <SearchableListItem isDisabled={option.isDisabled} prefix={option.icon}>
 *     <SearchableListItemBody description={option.description}>
 *       {option.label}
 *     </SearchableListItemBody>
 *   </SearchableListItem>
 * );
 *
 * <SearchableList options={options} renderOption={renderOption} />
 * ```
 *
 * @component
 */
export const SearchableListItem = React.forwardRef<
  HTMLElement,
  SearchableListItemProps
>(
  (
    {
      checkmarkStyle: checkmarkStyleProp,
      children,
      density: densityProp,
      isDisabled: isDisabledProp,
      ...props
    },
    ref
  ) => {
    const {
      checkmarkStyle: checkmarkStyleFromContext,
      Component,
      density: densityFromContext,
      hasMaxSelections,
      id,
      isActive,
      isDisabled: isSearchableListDisabled,
      isSelected,
      listIndex,
      itemProps,
      measureElement,
      value,
      verticalItemAlignment,
      virtualIndex,
    } = useSearchableListItemContext();

    const checkmarkStyle = checkmarkStyleProp ?? checkmarkStyleFromContext;
    const density = densityProp ?? densityFromContext;
    const isDisabled =
      isDisabledProp ||
      isSearchableListDisabled ||
      (!isSelected && hasMaxSelections);

    const mergedProps = mergeProps(itemProps, props);

    // @ts-expect-error - TS2322: The type for itemProps returned by Floating-UI is incorrect.
    const mergedRefs = useMergeRefs([ref, itemProps.ref, measureElement]);

    return (
      <Component
        {...mergedProps}
        _isActive={isActive}
        _listIndex={listIndex}
        checkmarkStyle={checkmarkStyle}
        data-value={value}
        {...(typeof virtualIndex === 'number'
          ? {'data-virtual-index': virtualIndex}
          : emptyObject)}
        density={density}
        id={id}
        isDisabled={isDisabled}
        isSelected={isSelected}
        ref={mergedRefs}
        role="option"
        tabIndex={-1}
        verticalItemAlignment={verticalItemAlignment}
      >
        {children}
      </Component>
    );
  }
);

export const SearchableListItemWrapper = React.memo(
  ({
    categoryLabel,
    density,
    option,
    renderOption,
    verticalItemAlignment,
    virtualIndex,
    measureElement = noop,
  }: {
    categoryLabel?: OptionGroupType['label'];
    density: SearchableListBoxProps['density'];
    option: OptionType;
    renderOption: SearchableListProps['renderOption'];
    verticalItemAlignment: SearchableListBoxProps['verticalItemAlignment'];
    virtualIndex?: number;
    measureElement?: ReturnType<typeof useVirtualizer>['measureElement'];
  }) => {
    const searchableListItem = useSearchableListItem(
      option.value,
      virtualIndex
    );

    const state = React.useMemo(
      () => ({
        ...searchableListItem.itemProps,
        categoryLabel,
        isActive: searchableListItem.isActive,
      }),
      [categoryLabel, searchableListItem.isActive, searchableListItem.itemProps]
    );

    const searchableListItemValues = React.useMemo(
      () => ({
        ...searchableListItem,
        density,
        verticalItemAlignment,
        measureElement,
      }),
      [density, searchableListItem, verticalItemAlignment, measureElement]
    );

    const renderedContent = React.useMemo(
      () => renderOption?.(option, state),
      [option, state, renderOption]
    );

    return (
      <SearchableListItemContext.Provider value={searchableListItemValues}>
        {renderedContent}
      </SearchableListItemContext.Provider>
    );
  }
);

SearchableListItemWrapper.displayName = 'SearchableListItemWrapper';
SearchableListItem.displayName = 'SearchableListItem';

export default SearchableListItem;
